import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import type { JoinIntent } from '../session';
import { isLoopbackHostname } from '@screenshare/shared';
import { Brand, PrivacyMark } from './Brand';
import { DevTunnelPanel } from './DevTunnelPanel';

export interface LandingPageProps {
  readonly busy: boolean;
  readonly busyRole: 'host' | 'viewer' | null;
  readonly captureSupported: boolean;
  readonly error: string | null;
  readonly joinIntent: JoinIntent | null;
  readonly onClearError: () => void;
  readonly onHost: (durationSeconds: number | null) => void;
  readonly onJoin: (roomCode: string, pin: string) => void;
}

export function LandingPage({
  busy,
  busyRole,
  captureSupported,
  error,
  joinIntent,
  onClearError,
  onHost,
  onJoin,
}: LandingPageProps) {
  const [roomCode, setRoomCode] = useState(joinIntent?.roomCode ?? '');
  const [pin, setPin] = useState('');
  const hasInvite = Boolean(joinIntent?.inviteToken);
  const [timed, setTimed] = useState(false);
  const [hours, setHours] = useState('1');
  const [minutes, setMinutes] = useState('0');
  const [seconds, setSeconds] = useState('0');
  const durationSeconds =
    Number(hours || 0) * 3_600 + Number(minutes || 0) * 60 + Number(seconds || 0);
  const validDuration = !timed || (durationSeconds >= 10 && durationSeconds <= 86_400);
  const showDevTunnel = import.meta.env.DEV && isLoopbackHostname(window.location.hostname);

  const updateDigits = (setter: (value: string) => void, maximum: number) =>
    ((event) => {
      setter(event.currentTarget.value.replace(/\D/gu, '').slice(0, maximum));
      onClearError();
    }) satisfies JSX.GenericEventHandler<HTMLInputElement>;

  const submit: JSX.SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    onJoin(roomCode, pin);
  };

  return (
    <main class="landing-shell">
      <header class="landing-nav">
        <Brand />
        <PrivacyMark />
      </header>

      <section class="landing" aria-labelledby="landing-title">
        <div class="landing__intro">
          <p class="eyebrow">Browser-native screen sharing</p>
          <h1 id="landing-title">
            Show your screen.
            <span>Keep it between you.</span>
          </h1>
          <p class="landing__lede">
            Crisp, direct screen sharing for two people. No account, no download, no recording.
          </p>
          <div class="landing__trust" aria-label="Session privacy">
            <span>Encrypted end-to-end</span>
            <span>Rooms auto-delete</span>
          </div>
        </div>

        <div class="entry-card">
          <fieldset class="duration-picker">
            <legend>Session duration</legend>
            <div class="duration-picker__modes">
              <button type="button" aria-pressed={!timed} onClick={() => setTimed(false)}>
                Until I stop
              </button>
              <button type="button" aria-pressed={timed} onClick={() => setTimed(true)}>
                Set a timer
              </button>
            </div>
            {timed ? (
              <div class="duration-fields" aria-describedby="duration-hint">
                <DurationField label="Hours" maximum={24} value={hours} onChange={setHours} />
                <span aria-hidden="true">:</span>
                <DurationField label="Minutes" maximum={59} value={minutes} onChange={setMinutes} />
                <span aria-hidden="true">:</span>
                <DurationField label="Seconds" maximum={59} value={seconds} onChange={setSeconds} />
              </div>
            ) : null}
            <p id="duration-hint" class={timed && !validDuration ? 'is-invalid' : ''}>
              {timed
                ? validDuration
                  ? 'Sharing stops automatically when this timer reaches zero.'
                  : 'Choose a duration from 10 seconds up to 24 hours.'
                : 'No time limit. Sharing ends only when you stop it.'}
            </p>
          </fieldset>

          <button
            class="primary-action"
            type="button"
            disabled={busy || !captureSupported || !validDuration}
            onClick={() => onHost(timed ? durationSeconds : null)}
          >
            <ScreenIcon />
            <span>{busy && busyRole === 'host' ? 'Preparing screen…' : 'Share screen'}</span>
            <ArrowIcon />
          </button>

          {!captureSupported ? (
            <p class="support-warning" role="status">
              Screen sharing is unavailable here. Open this page in a current desktop browser to
              host; you can still join a room.
            </p>
          ) : null}

          {showDevTunnel ? <DevTunnelPanel /> : null}

          <div class="entry-divider" aria-hidden="true">
            <span />
            or join a session
            <span />
          </div>

          <form class="join-form" onSubmit={submit} noValidate>
            <div class="field-group">
              <label for="room-code">Room code</label>
              <input
                id="room-code"
                name="room-code"
                value={roomCode}
                inputMode="numeric"
                autoComplete="off"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="000 000"
                aria-describedby="room-code-hint"
                disabled={busy}
                onInput={updateDigits(setRoomCode, 6)}
              />
              <span id="room-code-hint" class="field-hint">
                Six digits
              </span>
            </div>

            <div class="field-group">
              <label for="room-pin">PIN</label>
              <input
                id="room-pin"
                name="room-pin"
                value={pin}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{4}"
                maxLength={4}
                placeholder={hasInvite ? 'Not needed' : '0000'}
                disabled={busy || hasInvite}
                aria-describedby="room-pin-hint"
                onInput={updateDigits(setPin, 4)}
              />
              <span id="room-pin-hint" class="field-hint">
                {hasInvite ? 'Secure invite detected' : 'Four digits'}
              </span>
            </div>

            <button
              class="connect-action"
              type="submit"
              disabled={busy || roomCode.length !== 6 || (!hasInvite && pin.length !== 4)}
            >
              {busy && busyRole === 'viewer' ? 'Connecting…' : 'Connect'}
            </button>
          </form>

          {error ? (
            <div class="entry-error" role="alert">
              <AlertIcon />
              <span>{error}</span>
              <button type="button" aria-label="Dismiss error" onClick={onClearError}>
                ×
              </button>
            </div>
          ) : null}

          <p class="entry-card__footnote">One host · One viewer · Nothing stored</p>
        </div>
      </section>
    </main>
  );
}

interface DurationFieldProps {
  readonly label: string;
  readonly maximum: number;
  readonly value: string;
  readonly onChange: (value: string) => void;
}

function DurationField({ label, maximum, value, onChange }: DurationFieldProps) {
  return (
    <label>
      <span>{label}</span>
      <input
        value={value}
        type="number"
        inputMode="numeric"
        min="0"
        max={maximum}
        aria-label={label}
        onInput={(event) =>
          onChange(String(Math.min(maximum, Math.max(0, Number(event.currentTarget.value)))))
        }
      />
    </label>
  );
}

function ScreenIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="4" width="18" height="13" rx="2.5" />
      <path d="M8 21h8M12 17v4M12 13V8m0 0L9.5 10.5M12 8l2.5 2.5" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 8v5m0 3.5v.01M10.1 4.7 3.3 17a2 2 0 0 0 1.75 3h13.9a2 2 0 0 0 1.75-3L13.9 4.7a2.18 2.18 0 0 0-3.8 0Z" />
    </svg>
  );
}
